import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Badge,
  Popover,
  Chip,
  Button,
  Stack,
  alpha,
  Tab,
  Tabs,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  NotificationsNone as NotificationsIcon,
  Analytics as AnalyticsIcon,
  PersonAdd as PersonAddIcon,
  Upload as UploadIcon,
  EventNote as EventNoteIcon,
  Folder as FolderIcon,
  Mail as MailIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  DoneAll as DoneAllIcon,
  Circle as CircleIcon,
  Keyboard as KeyboardIcon,
  HelpOutline as HelpIcon,
  CreditCard as SubscriptionIcon,
  Schedule as ScheduleIcon,
  TrendingUp as ActivityIcon,
  WorkspacePremium as PremiumIcon
} from '@mui/icons-material';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { API_URL, UPCOMING_FEATURES } from '../config';
import Sidebar from '../components/Sidebar';
import FeedbackDialog from '../components/FeedbackDialog';
import AssistantChat from '../components/AssistantChat';
import { submitFeedback, fetchFeedback } from '../services/feedbackService';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from '../services/notificationService';

const drawerWidth = 64;
const ISSUE_TYPES_LABELS = {
  bug: 'Bug / Error',
  feature: 'Feature Request',
  ui: 'UI / UX Feedback',
  other: 'Other'
};

// Helper to get notification icon component
const getNotificationIconComponent = (type) => {
  const iconMap = {
    analysis_complete: <AnalyticsIcon fontSize="small" color="success" />,
    analysis_failed: <ErrorIcon fontSize="small" color="error" />,
    crew_added: <PersonAddIcon fontSize="small" color="info" />,
    crew_removed: <PersonAddIcon fontSize="small" color="warning" />,
    script_uploaded: <UploadIcon fontSize="small" color="primary" />,
    script_version: <UploadIcon fontSize="small" color="primary" />,
    callsheet_saved: <EventNoteIcon fontSize="small" color="success" />,
    project_update: <FolderIcon fontSize="small" color="info" />,
    invitation: <MailIcon fontSize="small" color="secondary" />,
    system: <InfoIcon fontSize="small" />,
    info: <InfoIcon fontSize="small" color="info" />
  };
  return iconMap[type] || <NotificationsIcon fontSize="small" />;
};

// Status options for user presence
const STATUS_OPTIONS = [
  { value: 'online', label: 'Online', color: '#22c55e' },
  { value: 'away', label: 'Away', color: '#f59e0b' },
  { value: 'busy', label: 'Busy', color: '#ef4444' },
  { value: 'offline', label: 'Appear Offline', color: '#6b7280' }
];

// Keyboard shortcuts reference
const KEYBOARD_SHORTCUTS = [
  { keys: ['Ctrl', 'S'], description: 'Save (in editors)' },
  { keys: ['Ctrl', 'Z'], description: 'Undo' },
  { keys: ['Ctrl', 'Y'], description: 'Redo' },
  { keys: ['Ctrl', 'A'], description: 'Select all' },
  { keys: ['Ctrl', '+'], description: 'Zoom in (Mind Map)' },
  { keys: ['Ctrl', '-'], description: 'Zoom out (Mind Map)' },
  { keys: ['Delete'], description: 'Delete selected' },
  { keys: ['Escape'], description: 'Cancel / Close' },
  { keys: ['G'], description: 'Toggle grid (Canvas)' },
  { keys: ['M'], description: 'Toggle mic (Calls)' },
  { keys: ['V'], description: 'Toggle video (Calls)' }
];

function MainLayout({ children }) {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [updatesAnchorEl, setUpdatesAnchorEl] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackLog, setFeedbackLog] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const upcomingFeatures = Array.isArray(UPCOMING_FEATURES) ? UPCOMING_FEATURES : [];
  
  // Dynamic notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationTab, setNotificationTab] = useState(0); // 0 = notifications, 1 = updates
  
  // User status state
  const [userStatus, setUserStatus] = useState(() => {
    return localStorage.getItem('userStatus') || 'online';
  });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  
  // User stats state
  const [projectCount, setProjectCount] = useState(0);
  
  // Fetch actual project count
  useEffect(() => {
    const fetchProjectCount = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/v1/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProjectCount(Array.isArray(data) ? data.length : 0);
        }
      } catch (err) {
        console.error('Failed to fetch project count:', err);
      }
    };
    fetchProjectCount();
  }, [user]);
  
  const userStats = {
    projectsCount: projectCount,
    plan: user?.plan || 'Free',
    role: 'Creator'
  };
  
  // Combined count for badge (unread notifications + upcoming features count)
  const totalBadgeCount = unreadCount + upcomingFeatures.length;
  
  // Handle status change
  const handleStatusChange = (newStatus) => {
    setUserStatus(newStatus);
    localStorage.setItem('userStatus', newStatus);
    setStatusMenuAnchor(null);
  };
  
  // Get current status info
  const currentStatus = STATUS_OPTIONS.find(s => s.value === userStatus) || STATUS_OPTIONS[0];

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      setNotificationsLoading(true);
      const data = await getNotifications({ limit: 20 });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user]);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Handle marking notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Handle marking all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getStatusStyles = (status, theme) => {
    const palette = {
      'Now Available': { color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.12) },
      'In QA': { color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.12) },
      'In Development': { color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.12) },
      'Work in Progress': { color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.12) },
      'Design Review': { color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.12) },
      'Prototype': { color: theme.palette.secondary.main, bg: alpha(theme.palette.secondary.main, 0.12) },
      'Research': { color: theme.palette.text.secondary, bg: alpha(theme.palette.text.secondary, 0.12) }
    };
    return palette[status] || { color: theme.palette.text.primary, bg: alpha(theme.palette.primary.main, 0.08) };
  };

  const loadFeedback = async () => {
    if (!user?.isSuperuser) {
      setFeedbackLog([]);
      return;
    }
    try {
      setFeedbackLoading(true);
      const response = await fetchFeedback();
      setFeedbackLog(response?.feedback || []);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  useEffect(() => {
    if (user?.isSuperuser) {
      loadFeedback();
    } else {
      setFeedbackLog([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isSuperuser]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
    handleClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleUpdatesOpen = (event) => {
    setUpdatesAnchorEl(event.currentTarget);
  };

  const handleUpdatesClose = () => {
    setUpdatesAnchorEl(null);
  };

  const handleFeedbackSubmit = async (payload) => {
    try {
      await submitFeedback(payload);
      setFeedbackOpen(false);
      if (user?.isSuperuser) {
        await loadFeedback();
      }
      alert('Thanks! Your feedback was captured for the product team.');
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : theme.palette.background.default
    }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : theme.palette.background.default,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? alpha('#1a1a1a', 0.8)
              : alpha('#fff', 0.8),
            backdropFilter: 'blur(20px)',
            borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.4)'
              : '0 2px 8px rgba(0,0,0,0.06)',
            color: (theme) => theme.palette.text.primary
          }}
        >
          <Toolbar>
            {/* User Menu */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              ml: 'auto',
              gap: 1
            }}>
              <Tooltip title="Notifications & updates" arrow>
                <span>
                  <IconButton
                    onClick={handleUpdatesOpen}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'info.main',
                        backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08)
                      }
                    }}
                  >
                    <Badge
                      badgeContent={totalBadgeCount}
                      color="secondary"
                      overlap="circular"
                      max={9}
                      invisible={!totalBadgeCount}
                    >
                      <NotificationsIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Send feedback" arrow>
                <IconButton
                  onClick={() => setFeedbackOpen(true)}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'secondary.main',
                      backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08)
                    }
                  }}
                >
                  <ModeCommentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <AssistantChat />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
                }
              }}
              onClick={handleMenu}
              >
                {user?.name ? (
                  <Avatar sx={{ 
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    width: 28, 
                    height: 28,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}>
                    {user.name[0]}
                  </Avatar>
                ) : (
                  <AccountIcon />
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    letterSpacing: '-0.2px',
                    fontSize: '0.8125rem'
                  }}
                >
                  {user?.name || 'Guest'}
                </Typography>
              </Box>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    mt: 1.5,
                    minWidth: 240,
                    maxWidth: 300,
                    borderRadius: 2,
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? alpha('#1a1a1a', 0.98)
                      : '#fff',
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                      ? '0 12px 32px rgba(0,0,0,0.6)'
                      : '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    '& .MuiMenuItem-root': {
                      borderRadius: 1.5,
                      mx: 1,
                      my: 0.25,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              >
                {/* User Info Header */}
                <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar sx={{ 
                        width: 40, 
                        height: 40,
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}>
                        {user?.name?.[0] || 'U'}
                      </Avatar>
                      {/* Status indicator dot */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 2,
                          right: 2,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: currentStatus.color,
                          border: '2px solid',
                          borderColor: 'background.paper'
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {user?.name || 'User'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {user?.email || 'user@example.com'}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={userStats.role}
                        sx={{ 
                          mt: 0.5, 
                          height: 20, 
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main'
                        }} 
                      />
                    </Box>
                  </Box>
                  
                  {/* Status Selector */}
                  <Box 
                    onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                    sx={{ 
                      mt: 1.5, 
                      p: 1, 
                      borderRadius: 2,
                      backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.5),
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.8)
                      }
                    }}
                  >
                    <CircleIcon sx={{ fontSize: 10, color: currentStatus.color }} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {currentStatus.label}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                      Change
                    </Typography>
                  </Box>
                </Box>

                {/* Quick Stats */}
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1 }}>
                        {userStats.projectsCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Projects
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <ActivityIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                          Active
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Account/Plan Info */}
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PremiumIcon sx={{ fontSize: 16, color: userStats.plan === 'Pro' ? 'warning.main' : 'text.secondary' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {userStats.plan} Plan
                      </Typography>
                      {userStats.plan === 'Free' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Upgrade for more features
                        </Typography>
                      )}
                    </Box>
                    {userStats.plan === 'Free' && (
                      <Chip 
                        size="small" 
                        label="Upgrade" 
                        color="primary" 
                        sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
                        onClick={(e) => { e.stopPropagation(); handleClose(); navigate('/settings?section=billing'); }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Menu Items */}
                <Box sx={{ py: 0.5 }}>
                  <MenuItem
                    onClick={() => {
                      toggleDarkMode();
                      handleClose();
                    }}
                  >
                    <ListItemIcon>
                      {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>
                      {darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleProfile}>
                    <ListItemIcon>
                      <AccountIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Profile</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleSettings}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Settings</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setShortcutsDialogOpen(true); handleClose(); }}>
                    <ListItemIcon>
                      <KeyboardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Keyboard Shortcuts</ListItemText>
                    <Typography variant="caption" color="text.disabled">?</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => { window.open('https://docs.luminara.app', '_blank'); handleClose(); }}>
                    <ListItemIcon>
                      <HelpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Help & Support</ListItemText>
                  </MenuItem>
                </Box>
                
                <Divider />
                <Box sx={{ py: 0.5 }}>
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Sign Out</ListItemText>
                  </MenuItem>
                </Box>
              </Menu>

              {/* Status Selection Menu */}
              <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={() => setStatusMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    minWidth: 160,
                    borderRadius: 2,
                    mt: 0.5
                  }
                }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem 
                    key={status.value} 
                    onClick={() => handleStatusChange(status.value)}
                    selected={userStatus === status.value}
                  >
                    <CircleIcon sx={{ fontSize: 10, color: status.color, mr: 1.5 }} />
                    <Typography variant="body2">{status.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        <Popover
          open={Boolean(updatesAnchorEl)}
          anchorEl={updatesAnchorEl}
          onClose={handleUpdatesClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              width: 360,
              maxHeight: 460,
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? alpha('#1f1f1f', 0.95)
                : '#fff',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.3 : 0.15)}`,
              boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 15px 35px rgba(0,0,0,0.6)'
                : '0 12px 28px rgba(15,23,42,0.25)',
              overflow: 'hidden'
            }
          }}
        >
          {/* Tabs for Notifications vs Updates */}
          <Tabs
            value={notificationTab}
            onChange={(e, newVal) => setNotificationTab(newVal)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }
            }}
          >
            <Tab 
              label={
                <Badge badgeContent={unreadCount} color="error" max={9}>
                  <span>Notifications</span>
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={upcomingFeatures.length} color="warning" max={9}>
                  <span>Updates</span>
                </Badge>
              } 
            />
          </Tabs>

          {/* Notifications Tab */}
          {notificationTab === 0 && (
            <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              {unreadCount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    size="small"
                    startIcon={<DoneAllIcon />}
                    onClick={handleMarkAllAsRead}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Mark all as read
                  </Button>
                </Box>
              )}
              {notificationsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No notifications yet
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {notifications.map((notification) => (
                    <ListItem
                      key={notification._id}
                      onClick={() => !notification.read && handleMarkAsRead(notification._id)}
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        cursor: notification.read ? 'default' : 'pointer',
                        backgroundColor: (theme) => notification.read
                          ? 'transparent'
                          : alpha(theme.palette.primary.main, 0.08),
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12)
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getNotificationIconComponent(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                            {notification.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            {notification.message && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {notification.message}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.disabled">
                              {new Date(notification.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                      />
                      {!notification.read && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main', ml: 1 }} />
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Updates Tab */}
          {notificationTab === 1 && (
            <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Product Updates
                </Typography>
                <Chip label="Alpha" size="small" color="warning" sx={{ fontWeight: 600 }} />
              </Box>
              <List dense sx={{ py: 0 }}>
                {upcomingFeatures.map((feature) => (
                  <ListItem
                    key={feature.id}
                    disableGutters
                    sx={{
                      mb: 1.25,
                      borderRadius: 2.5,
                      px: 1.5,
                      py: 1.5,
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      background: (theme) =>
                        theme.palette.mode === 'dark'
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.background.paper, 0.4)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha('#fff', 0.9)})`,
                      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? `0 10px 24px ${alpha('#000', 0.6)}`
                        : `0 8px 18px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                        gap: 1
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {feature.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={feature.status}
                        sx={(theme) => {
                          const styles = getStatusStyles(feature.status, theme);
                          return {
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: styles.color,
                            backgroundColor: styles.bg,
                            borderColor: styles.color
                          };
                        }}
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, lineHeight: 1.5, wordBreak: 'break-word' }}
                    >
                      {feature.description}
                    </Typography>
                    {feature.id !== 'feedback.reportIssuesButton' && feature.status !== 'Now Available' && (
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: 'italic', color: 'text.disabled' }}
                      >
                        {feature.status === 'In QA'
                          ? 'Code complete • Enabling soon'
                          : feature.status === 'Work in Progress'
                          ? 'Active development • Coming soon'
                          : 'Sneak peek • Early access soon'}
                      </Typography>
                    )}
                  </ListItem>
                ))}
                {upcomingFeatures.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    Nothing to announce yet — stay tuned!
                  </Typography>
                )}
              </List>
              <Divider sx={{ my: 1.5 }} />
              {user?.isSuperuser && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Recent Feedback
                  </Typography>
                  {feedbackLoading ? (
                    <Typography variant="caption" color="text.secondary">
                      Loading feedback...
                    </Typography>
                  ) : feedbackLog.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No submissions yet — be the first to report something.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25} sx={{ maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
                      {feedbackLog.map((item) => (
                        <Box
                          key={item._id || item.id}
                          sx={{
                            borderRadius: 2,
                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                            p: 1.25,
                            backgroundColor: (theme) => alpha(theme.palette.background.default, 0.7)
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.createdAt).toLocaleString()} • {ISSUE_TYPES_LABELS[item.type] || 'Feedback'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.summary}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.details}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Popover>
        <FeedbackDialog
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          onSubmit={handleFeedbackSubmit}
        />

        {/* Keyboard Shortcuts Dialog */}
        <Dialog
          open={shortcutsDialogOpen}
          onClose={() => setShortcutsDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <KeyboardIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Keyboard Shortcuts</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <List dense>
              {KEYBOARD_SHORTCUTS.map((shortcut, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={shortcut.description}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {shortcut.keys.map((key, keyIndex) => (
                      <React.Fragment key={keyIndex}>
                        <Chip
                          label={key}
                          size="small"
                          sx={{
                            height: 24,
                            minWidth: 32,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            backgroundColor: (theme) => alpha(theme.palette.action.selected, 0.8),
                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`
                          }}
                        />
                        {keyIndex < shortcut.keys.length - 1 && (
                          <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center' }}>+</Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Press <strong>?</strong> anywhere to show this dialog
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShortcutsDialogOpen(false)} variant="contained" size="small">
              Got it
            </Button>
          </DialogActions>
        </Dialog>
        <Box sx={{ flex: 1, overflow: 'auto', mt: '48px' }}>
          <Box
            sx={{
              py: 0,
              px: 0,
              minHeight: 'calc(100vh - 48px)',
              height: '100%',
              width: '100%'
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default MainLayout;
